import {
  buildBundleQuantityPlan,
  executeBundleQuantityPlan,
} from './cart-bundle-quantity.js';
import {
  isCartMutationPending,
  isCartRecoveryRequired,
  lockCartMutationRecovery,
  runRecoverableCartMutation,
  subscribeCartMutationState,
} from './cart-mutation-coordinator.js';
import { initializeCartRecommendations } from './cart-recommendations.js';
import {
  CartRecoveryRequiredError,
  awaitCartMutationResponse,
  isCartMutationUncertain,
  readCartMutationJson,
  recoverAuthoritativeCart,
  validateAuthoritativeCartSnapshot,
} from './cart-response-recovery.js';
import { syncTierShipping } from './cart-tier-shipping.js?v=20261012-owner-display';
import {
  initializePrivateMysteryCheckoutGuard,
  runPrivateMysteryLineMutation,
} from './private-mystery-hold.js';

const routesRoot = window.Shopify?.routes?.root || '/';
const pendingUpdates = new Map();
const failedUpdates = new Map();
let updateTimer = 0;
let updateInFlight = false;

initializePrivateMysteryCheckoutGuard();

const getPage = () => document.querySelector('[data-cart-page]');
const getRoute = () => document.querySelector('[data-route-entry-root="cart"]');
const escapeSelector = (value) => window.CSS?.escape?.(value) || value.replace(/["\\]/g, '\\$&');
const getLine = (key, root = getPage()) => root?.querySelector(`[data-cart-line-item][data-cart-key="${escapeSelector(key)}"]`);

const syncCheckoutPending = (pending = isCartMutationPending()) => {
  getPage()?.querySelectorAll('[name="checkout"]').forEach((control) => {
    if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;

    if (pending) {
      if (!control.disabled) control.dataset.cartMutationDisabled = 'true';
      control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
      return;
    }

    if (control.dataset.cartMutationDisabled === 'true') {
      control.disabled = false;
      delete control.dataset.cartMutationDisabled;
      control.removeAttribute('aria-disabled');
    }
  });
};

subscribeCartMutationState(syncCheckoutPending);

const initializeBundleQuantityControls = (root = getPage()) => {
  root?.querySelectorAll('[data-cart-bundle-parent-control]').forEach((control) => {
    const lineItem = control.closest('[data-cart-line-item]');
    const quantity = Number(lineItem?.dataset.cartQuantity) || 1;

    if (control instanceof HTMLInputElement) {
      control.readOnly = false;
      control.removeAttribute('aria-readonly');
      return;
    }

    if (control instanceof HTMLButtonElement) {
      control.disabled = control.dataset.cartAction === 'decrease' && quantity <= 1;
    }
  });
};

const findBundleParentLine = ({ bundleInstance, parentVariantId }, root = getPage()) => (
  [...(root?.querySelectorAll('[data-cart-line-item]') || [])].find((lineItem) => (
    lineItem.dataset.cartBundleRole === 'parent'
    && lineItem.dataset.cartBundleInstance === bundleInstance
    && lineItem.dataset.cartVariantId === parentVariantId
  ))
);

const getBundleQuantityPlan = (parentLine, nextParentQuantity, root = getPage()) => {
  if (
    !(parentLine instanceof HTMLElement)
    || parentLine.dataset.cartBundleRole !== 'parent'
    || !parentLine.hasAttribute('data-cart-has-per-parent-bundle-child')
  ) {
    return null;
  }

  const parentKey = parentLine.dataset.cartKey || '';
  const bundleInstance = parentLine.dataset.cartBundleInstance || '';
  const children = [...(root?.querySelectorAll('[data-cart-line-item][data-cart-parent-key]') || [])]
    .filter((lineItem) => (
      lineItem.dataset.cartParentKey === parentKey
      && lineItem.dataset.cartBundleRole === 'add_on'
      && lineItem.dataset.cartBundleInstance === bundleInstance
    ))
    .map((lineItem) => ({
      key: lineItem.dataset.cartKey,
      variantId: lineItem.dataset.cartVariantId,
      bundleInstance: lineItem.dataset.cartBundleInstance,
      currentQuantity: Number(lineItem.dataset.cartQuantity),
      componentQuantity: Number(lineItem.dataset.cartComponentQuantity),
      quantityMode: lineItem.dataset.cartQuantityMode,
      bundleGroup: lineItem.dataset.cartBundleGroup,
    }));

  return buildBundleQuantityPlan({
    parentKey,
    parentVariantId: parentLine.dataset.cartVariantId,
    bundleInstance,
    parentQuantity: Number(parentLine.dataset.cartQuantity),
    nextParentQuantity,
    children,
  });
};

const getEntryLine = (entry, root = getPage()) => (
  entry.bundleInstance
    ? findBundleParentLine(entry, root)
    : getLine(entry.key, root)
);

const getStickyHeader = () => (
  document.querySelector('[data-header-scroll-shell]')
  || document.querySelector('[data-header-scroll-reveal]')?.closest('.shopify-section')
);

const syncStickyHeaderHeight = () => {
  const header = getStickyHeader();
  const height = header instanceof HTMLElement ? Math.round(header.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty('--cart-page-sticky-header-height', `${Math.max(0, height)}px`);
};

syncStickyHeaderHeight();

if ('ResizeObserver' in window) {
  const header = getStickyHeader();
  if (header) new ResizeObserver(syncStickyHeaderHeight).observe(header);
} else {
  window.addEventListener('resize', syncStickyHeaderHeight, { passive: true });
}

const syncHeaderCart = (cart) => {
  const itemCount = Number(cart.item_count) || 0;

  document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
    let count = trigger.querySelector('.site-header__cart-count');
    const countHost = trigger.querySelector('.site-header__utility-icon') || trigger;

    if (itemCount > 0 && !count) {
      count = document.createElement('span');
      count.className = 'site-header__cart-count';
      count.setAttribute('aria-hidden', 'true');
      countHost?.append(count);
    }

    if (count) {
      count.textContent = String(Math.min(itemCount, 99));
      count.dataset.countDigits = itemCount > 9 ? '2' : '1';
      count.hidden = itemCount === 0;
    }

    trigger.setAttribute('aria-label', itemCount > 0
      ? `Bag, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`
      : 'Bag');

    const total = trigger.querySelector('[data-cart-total]');
    if (total && cart.total_price != null) {
      total.textContent = getPage()?.querySelector('[data-cart-page-content]')?.dataset.cartTotal || total.textContent;
    }
  });
};

const setOptimisticQuantity = (key, quantity) => {
  const line = getLine(key);
  const input = line?.querySelector('[data-cart-key].cart-line__quantity-input');
  if (!(input instanceof HTMLInputElement)) return;

  input.value = String(quantity);
  line.dataset.cartPendingQuantity = String(quantity);
  const decrease = line.querySelector('[data-cart-action="decrease"]');
  if (!decrease?.hasAttribute('data-cart-bundle-child-control')) {
    decrease?.toggleAttribute('disabled', quantity <= 1);
  }
};

const setOptimisticBundleQuantity = (plan) => {
  plan.changes.forEach((change) => setOptimisticQuantity(change.key, change.quantity));
};

const isRetryableCartError = (error) => (
  Boolean(error?.retryable)
  || /failed to fetch|networkerror|load failed|unexpected token|json/i.test(error?.message || '')
);

const showError = (key, error, quantity, action = 'input') => {
  const line = getLine(key);
  const input = line?.querySelector('.cart-line__quantity-input');
  const control = action === 'input'
    ? input
    : line?.querySelector(`[data-cart-action="${action}"]`) || input;
  const feedback = line?.querySelector('[data-cart-inline-error]');
  const message = feedback?.querySelector('[data-cart-error-message]');
  const retry = feedback?.querySelector('[data-cart-error-retry]');
  const retryable = isRetryableCartError(error);
  const recoveryRequired = Boolean(error?.recoveryRequired);

  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-invalid', 'false');
    input.setCustomValidity('');
  }
  if (message) message.textContent = error?.message || 'We couldn’t update that quantity. Please try again.';
  if (retry) {
    retry.hidden = !retryable && !recoveryRequired;
    retry.textContent = recoveryRequired ? 'Reload bag' : 'Try again';
    retry.toggleAttribute('data-cart-recovery-reload', recoveryRequired);
  }
  if (feedback) feedback.hidden = false;
  if (line) line.removeAttribute('aria-busy');
  window.SigmaErrorFeedback?.show({
    target: control,
    wrapper: feedback?.parentElement,
    message: feedback,
  });
  feedback?.scrollIntoView({ block: 'nearest' });
  if (retryable && !recoveryRequired) {
    failedUpdates.set(key, quantity);
  } else {
    failedUpdates.delete(key);
  }
};

const showQuantityValidationError = (input) => {
  const line = input.closest('[data-cart-line-item]');
  const feedback = line?.querySelector('[data-cart-inline-error]');
  const message = feedback?.querySelector('[data-cart-error-message]');
  const retry = feedback?.querySelector('[data-cart-error-retry]');
  const validationMessage = 'Enter a whole number of 1 or more.';

  input.setAttribute('aria-invalid', 'true');
  input.setCustomValidity(validationMessage);
  if (message) message.textContent = validationMessage;
  if (retry) retry.hidden = true;
  if (feedback) feedback.hidden = false;
  window.SigmaErrorFeedback?.show({
    target: input,
    wrapper: feedback?.parentElement,
    message: feedback,
  });
  input.focus({ preventScroll: true });
};

const parseUpdatedRoute = (sectionHtml) => {
  const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
  return parsed.querySelector('[data-route-entry-root="cart"]');
};

const renderPage = (sectionHtml, focusState = {}, { rewardRefresh = false } = {}) => {
  const currentRoute = getRoute();
  const nextRoute = parseUpdatedRoute(sectionHtml);
  if (!currentRoute || !nextRoute) throw new Error('The bag could not be refreshed. Please try again.');

  const scrollY = window.scrollY;
  const previousShipping = currentRoute.querySelector('[data-cart-shipping]');
  syncTierShipping(nextRoute, { previousShipping });

  if (rewardRefresh) {
    const currentVoucherSlot = currentRoute.querySelector('[data-loyalty-redemption-slot]');
    const nextVoucherSlot = nextRoute.querySelector('[data-loyalty-redemption-slot]');
    const reservedVoucherBlockSize = currentVoucherSlot?.style.getPropertyValue('min-block-size');
    const currentLoyaltySummary = currentVoucherSlot?.closest('sigma-loyalty-summary');
    const nextLoyaltySummary = nextVoucherSlot?.closest('sigma-loyalty-summary');
    const currentLoyaltyBlockSize = currentLoyaltySummary?.getBoundingClientRect().height || 0;

    if (nextVoucherSlot && reservedVoucherBlockSize) {
      nextVoucherSlot.style.setProperty('min-block-size', reservedVoucherBlockSize);
    }

    if (nextLoyaltySummary && currentLoyaltyBlockSize > 0) {
      nextLoyaltySummary.dataset.loyaltyReplacementHeight = 'true';
      nextLoyaltySummary.style.setProperty('min-block-size', `${currentLoyaltyBlockSize}px`);
    }
  }

  currentRoute.replaceWith(nextRoute);
  const page = getPage();
  syncCheckoutPending();
  initializeBundleQuantityControls(page);
  initializeCartRecommendations(page);

  pendingUpdates.forEach((entry) => {
    const line = getEntryLine(entry, page);

    if (!line) return;
    entry.key = line.dataset.cartKey || entry.key;

    const plan = getBundleQuantityPlan(line, entry.quantity, page);
    if (plan) {
      setOptimisticBundleQuantity(plan);
    } else {
      setOptimisticQuantity(entry.key, entry.quantity);
    }
  });

  if (!rewardRefresh) {
    const focusLine = getEntryLine(focusState, page);
    const focusTarget = focusState.action === 'input'
      ? focusLine?.querySelector('.cart-line__quantity-input')
      : focusLine?.querySelector(`[data-cart-action="${focusState.action}"]`);
    focusTarget?.focus({ preventScroll: true });
  }
  window.scrollTo({ top: scrollY, behavior: 'auto' });
};

const sectionId = () => getRoute()?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '') || 'main';

const requestCartLineChange = async ({ key, quantity }, pageSectionId) => {
  const cart = await runPrivateMysteryLineMutation(getLine(key), quantity, async () => {
    const response = await awaitCartMutationResponse(
      fetch(`${routesRoot}cart/change.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: [pageSectionId, 'cart-drawer'],
          sections_url: window.location.pathname,
        }),
      }),
    );
    const responseCart = await readCartMutationJson(response, 'We couldn’t read the bag update. Please try again.');

    if (!response.ok) {
      const error = new Error(
        responseCart.description || responseCart.message || 'We couldn’t update that quantity. Please try again.',
      );
      error.retryable = response.status === 429 || response.status >= 500;
      throw error;
    }

    return responseCart;
  });

  const updatedLine = Array.isArray(cart.items)
    ? cart.items.find((item) => item?.key === key)
    : null;
  const updatedQuantity = Number(updatedLine?.quantity);

  if (Number.isInteger(updatedQuantity) && updatedQuantity < quantity) {
    const error = new Error(
      updatedQuantity > 0
        ? `Only ${updatedQuantity} available. Your bag has been updated to ${updatedQuantity}.`
        : 'This item is no longer available. Your bag has been refreshed.',
    );
    error.rollbackCart = cart;
    throw error;
  }

  return cart;
};

const fetchRenderedSection = async (renderedSectionId) => {
  const sectionUrl = new URL(window.location.href);
  sectionUrl.search = '';
  sectionUrl.searchParams.set('section_id', renderedSectionId);
  const response = await fetch(sectionUrl, {
    cache: 'no-store',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) {
    throw new Error('The bag could not be refreshed. Please reload the page.');
  }

  return response.text();
};

const updateDrawerSection = async (sectionHtml, guard = {}) => {
  const pendingRenders = [];
  window.dispatchEvent(new CustomEvent('sigma:cart-page-section-update', {
    detail: {
      sectionHtml,
      isCurrent: guard.isCurrent,
      waitUntil: (render) => pendingRenders.push(Promise.resolve(render)),
    },
  }));
  await Promise.all(pendingRenders);
};

const recoverCartSurfaces = async (pageSectionId, error, focusState) => {
  return recoverAuthoritativeCart({
    error,
    readCart: async () => {
      const [pageSection, drawerSection, cartResponse] = await Promise.all([
        fetchRenderedSection(pageSectionId),
        fetchRenderedSection('cart-drawer'),
        fetch(`${routesRoot}cart.js`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }),
      ]);

      if (!cartResponse.ok) {
        throw new Error('The bag could not be refreshed. Please reload the page.');
      }

      return {
        cart: validateAuthoritativeCartSnapshot(await cartResponse.json()),
        pageSection,
        drawerSection,
      };
    },
    renderCart: async ({ cart, pageSection, drawerSection }, guard) => {
      if (!guard.isCurrent()) return;
      renderPage(pageSection, focusState);
      if (!guard.isCurrent()) return;
      syncHeaderCart(cart);
      await updateDrawerSection(drawerSection, guard);
    },
  });
};

const flushUpdates = async () => {
  if (updateInFlight || pendingUpdates.size === 0) return;

  const [pendingId, entry] = pendingUpdates.entries().next().value;
  pendingUpdates.delete(pendingId);
  updateInFlight = true;

  try {
    await runRecoverableCartMutation(async () => {
      try {
        const entryLine = getEntryLine(entry);

        if (!entryLine) {
          throw new Error('That bag item changed before it could be updated. Review your bag and try again.');
        }

        entry.key = entryLine.dataset.cartKey || entry.key;
        entryLine.setAttribute('aria-busy', 'true');
        entryLine.querySelector(`[data-cart-action="${entry.action}"]`)?.setAttribute('aria-busy', 'true');

        const pageSectionId = sectionId();
        const bundlePlan = getBundleQuantityPlan(entryLine, entry.quantity);
        const cart = bundlePlan?.changes?.length
          ? await executeBundleQuantityPlan(
            bundlePlan,
            (change) => requestCartLineChange(change, pageSectionId),
          )
          : await requestCartLineChange({ key: entry.key, quantity: entry.quantity }, pageSectionId);

        const pageSection = cart.sections?.[pageSectionId];
        if (!pageSection) throw new Error('The bag updated, but the page could not be refreshed. Please reload.');

        renderPage(pageSection, entry);
        syncHeaderCart(cart);
        failedUpdates.delete(entry.key);
        getPage()?.querySelector('[data-cart-status]')?.replaceChildren(
          bundlePlan ? `Bundled quantity updated to ${entry.quantity}.` : `Quantity updated to ${entry.quantity}.`,
        );

        await updateDrawerSection(cart.sections?.['cart-drawer']);
      } catch (caughtError) {
        let error = caughtError;
        const pageSectionId = sectionId();
        let recoverySucceeded = false;

        try {
          await recoverCartSurfaces(pageSectionId, caughtError, entry);
          recoverySucceeded = true;
        } catch (recoveryError) {
          error = recoveryError;
          if (recoveryError?.recoveryRequired) {
            lockCartMutationRecovery(recoveryError.lateRecovery);
          }
          const currentLine = getEntryLine(entry);
          const serverQuantity = Number(currentLine?.dataset.cartQuantity) || 1;
          if (currentLine) setOptimisticQuantity(currentLine.dataset.cartKey, serverQuantity);
        }

        if (recoverySucceeded && isCartMutationUncertain(caughtError)) {
          error = new Error(
            'We couldn’t confirm the update response. Your bag has been refreshed. Review it before checkout.',
          );
        } else if (recoverySucceeded && caughtError?.rollbackError) {
          error = new Error(
            'We couldn’t keep those bundled quantities together. Your bag has been refreshed. Review it before checkout.',
            { cause: caughtError },
          );
        }

        const currentLine = getEntryLine(entry);
        const currentKey = currentLine?.dataset.cartKey || entry.key;
        if (currentLine) {
          showError(currentKey, error, entry.quantity, entry.action);
        } else {
          getPage()?.querySelector('[data-cart-status]')?.replaceChildren(error.message);
        }
      }
    }, (queueError) => {
      const currentLine = getEntryLine(entry);
      const currentKey = currentLine?.dataset.cartKey || entry.key;
      if (currentLine) {
        showError(currentKey, queueError, entry.quantity, entry.action);
      } else {
        getPage()?.querySelector('[data-cart-status]')?.replaceChildren(queueError.message);
      }
      return false;
    });
  } finally {
    updateInFlight = false;
    if (pendingUpdates.size > 0) flushUpdates();
  }
};

const scheduleUpdate = (key, quantity, action = 'input') => {
  if (!key || !Number.isInteger(quantity) || quantity < 1) return;

  if (isCartRecoveryRequired()) {
    showError(key, new CartRecoveryRequiredError(), quantity, action);
    return;
  }

  failedUpdates.delete(key);
  const line = getLine(key);
  if (!line || line.dataset.cartBundleRole === 'add_on') return;
  const input = line?.querySelector('.cart-line__quantity-input');
  const feedback = line?.querySelector('[data-cart-inline-error]');
  if (input instanceof HTMLInputElement) {
    input.setAttribute('aria-invalid', 'false');
    input.setCustomValidity('');
  }
  window.SigmaErrorFeedback?.clear({
    target: input,
    wrapper: feedback?.parentElement,
    message: feedback,
  });
  if (feedback) feedback.hidden = true;

  let bundlePlan = null;

  try {
    bundlePlan = getBundleQuantityPlan(line, quantity);
  } catch (error) {
    showError(key, error, quantity, action);
    return;
  }

  const entry = {
    key,
    quantity,
    action,
    bundleInstance: bundlePlan?.bundleInstance || '',
    parentVariantId: bundlePlan?.parentVariantId || '',
  };

  if (bundlePlan) {
    setOptimisticBundleQuantity(bundlePlan);
  } else {
    setOptimisticQuantity(key, quantity);
  }

  const pendingId = bundlePlan
    ? `bundle:${bundlePlan.bundleInstance}:${bundlePlan.parentVariantId}`
    : key;
  pendingUpdates.set(pendingId, entry);
  window.clearTimeout(updateTimer);
  updateTimer = window.setTimeout(flushUpdates, 180);
};

initializeBundleQuantityControls(getPage());
initializeCartRecommendations(getPage());
syncTierShipping(getPage());

window.addEventListener('sigma:cart-drawer-page-section-update', (event) => {
  const render = Promise.resolve().then(() => {
    const sectionHtml = event.detail?.sectionHtml;
    if (!sectionHtml || !getPage()) return;
    if (event.detail?.isCurrent?.() === false) {
      throw new Error('A newer bag recovery replaced this render.');
    }
    renderPage(sectionHtml, event.detail?.focusState);
    if (event.detail?.isCurrent?.() === false) {
      throw new Error('A newer bag recovery replaced this render.');
    }
    if (event.detail?.cart) syncHeaderCart(event.detail.cart);
  });
  event.detail?.waitUntil?.(render);
});

window.addEventListener('sigma:issued-voucher-cart-refresh', (event) => {
  const pageSectionId = sectionId();
  const sectionHtml = event.detail?.sections?.[pageSectionId];
  if (!getPage() || typeof sectionHtml !== 'string' || !sectionHtml.trim()) return;

  const render = Promise.resolve().then(() => {
    if (event.detail?.signal?.aborted || event.detail?.isCurrent?.() === false) {
      throw new Error('The cart page refresh was cancelled.');
    }
    renderPage(sectionHtml, {}, { rewardRefresh: true });
    if (event.detail?.cart) syncHeaderCart(event.detail.cart);
  });
  event.detail?.acknowledge?.(pageSectionId, render);
});

document.addEventListener('click', (event) => {
  const control = event.target.closest('[data-cart-page-quantity-delta]');
  if (
    control instanceof HTMLButtonElement
    && !control.disabled
    && !updateInFlight
    && getPage()?.contains(control)
  ) {
    const input = document.getElementById(control.dataset.cartPageQuantityTarget || '');
    const delta = Number(control.dataset.cartPageQuantityDelta);
    if (!(input instanceof HTMLInputElement) || !Number.isInteger(delta)) return;

    const currentQuantity = Number(input.value);
    const quantity = Math.max(1, (Number.isInteger(currentQuantity) ? currentQuantity : 1) + delta);
    scheduleUpdate(control.dataset.cartKey, quantity, control.dataset.cartAction);
    return;
  }

  const retry = event.target.closest('[data-cart-page] [data-cart-error-retry]');
  if (retry?.hasAttribute('data-cart-recovery-reload')) {
    window.location.reload();
    return;
  }
  const line = retry?.closest('[data-cart-line-item]');
  const key = line?.dataset.cartKey;
  if (!updateInFlight && key && failedUpdates.has(key)) scheduleUpdate(key, failedUpdates.get(key), 'input');
});

document.addEventListener('change', (event) => {
  const input = event.target;
  if (
    !(input instanceof HTMLInputElement)
    || input.readOnly
    || updateInFlight
    || !input.matches('[data-cart-page] .cart-line__quantity-input')
  ) return;

  const quantity = Number(input.value);
  if (!Number.isInteger(quantity) || quantity < 1) {
    showQuantityValidationError(input);
    return;
  }

  scheduleUpdate(input.dataset.cartKey, quantity, 'input');
});

document.addEventListener('submit', (event) => {
  if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'CartPageForm') return;
  event.preventDefault();
  if (updateInFlight) return;

  getPage()?.querySelectorAll('.cart-line__quantity-input').forEach((input) => {
    const quantity = Number(input.value);
    const currentQuantity = Number(input.closest('[data-cart-line-item]')?.dataset.cartQuantity);
    if (Number.isInteger(quantity) && quantity >= 1 && quantity !== currentQuantity) {
      scheduleUpdate(input.dataset.cartKey, quantity, 'input');
    }
  });
});
